// services/provisioningService.js — additive SharePoint schema provisioning
//
// Brings every list the app uses up to the schema the code expects, without
// touching anything that is already there:
//   • missing lists are created
//   • missing columns are added
//   • key lookup / sort columns are indexed
//   • JSON-blob columns still stored as single-line text (255-char cap) are widened to multi-line
// Nothing is ever deleted, renamed or retyped. A column whose type differs from
// the expected one is reported and left as it is.
//
// Creating lists and columns needs the Sites.Manage.All delegated permission
// (provisionRequest), which only the provisioning owners are asked for.
import { getSiteId } from "./sharepointService";
import { provisionRequest, provisioningOwners, sharePointConfig } from "../authConfig";

export function isProvisioningOwner(email) {
  return provisioningOwners.includes((email || "").toLowerCase());
}

const graphBase = "https://graph.microsoft.com/v1.0";

// Bump when APP_SCHEMA changes so the automatic run happens again
export const SCHEMA_VERSION = "2026.11.1";
const RESULT_KEY = "lekoil_schema_provision";

// Column types: text (single line), note (multi-line plain), number, boolean, date (date only)
const col = (name, type = "text", extra = {}) => ({ name, type, ...extra });

export const APP_SCHEMA = [
  {
    list: sharePointConfig.listName, // SCMWeeklyData
    description: "SCM weekly aggregate records",
    columns: [col("Week_Ending", "date", { indexed: true }), col("FieldData", "note")],
  },
  {
    list: sharePointConfig.schemaListName, // SCMEntitySchema
    description: "Dashboard entity definitions",
    columns: [
      col("Title", "text", { indexed: true }),
      col("EntityId"), col("EntityName"), col("Category"), col("FieldType"), col("Icon"), col("Unit"),
      col("CardGradient"), col("ChartType"),
      col("ShowOnDashboard", "boolean"), col("ShowInChart", "boolean"), col("Active", "boolean"),
      col("SortOrder", "number", { indexed: true }),
    ],
  },
  {
    list: "SCMUserAccess",
    description: "App users and roles",
    columns: [
      col("Title", "text", { indexed: true }),
      col("UserName"), col("Role"), col("Department"),
      col("IsActive", "boolean"), col("GrantedBy"), col("GrantedAt"), col("Notes", "note"),
    ],
  },
  {
    list: "SCMActivities",
    description: "Activity tracker tasks",
    columns: [
      col("Description", "note"), col("Category", "text", { indexed: true }), col("Priority"),
      col("Status", "text", { indexed: true }), col("DueDate", "date", { indexed: true }),
      col("UserDept"), col("Entity"), col("Contractor"), col("LookAhead", "note"),
      col("OwnerEmail", "text", { indexed: true }), col("OwnerName"), col("OwnerJobTitle"), col("OwnerDepartment"),
      col("AssignedTo"), col("AssignedToName"),
      col("CreatedBy", "text", { indexed: true }), col("CreatedByName"), col("CreatedAt"), col("UpdatedAt"),
      col("UpdatesData", "note"), col("CFOIssue", "text", { indexed: true }), col("CFORecommendations", "note"),
    ],
  },
  {
    list: "SCMTrainingRoster",
    description: "Safety Moment & KSS roster",
    columns: [
      col("TrainingDate", "date", { indexed: true }), col("TrainingTime"),
      col("HandlerName"), col("HandlerEmail", "text", { indexed: true }), col("HandlerDept"),
      col("Venue"), col("Notes", "note"),
    ],
  },
  {
    list: "SCMBenchmarkDB",
    description: "Benchmark pricing database",
    columns: [
      col("Title", "text", { indexed: true }),
      col("Category", "text", { indexed: true }), col("Location"), col("CommodityGroup"),
      col("ServiceDescription", "note"), col("SupplierName", "text", { indexed: true }), col("UnitOfMeasure"),
      col("BenchmarkRate", "number"), col("LowestRate", "number"), col("AwardedRate", "number"),
      col("PercentageChange", "number"), col("Currency"),
      col("CommercialTerms", "note"), col("AwardJustification", "note"),
      col("TenderDate", "date", { indexed: true }), col("ContractDuration"),
      col("CostSavingAchieved", "number"), col("MarketObservation", "note"),
      col("Phase"), col("SubmittedBy"), col("SubmittedByName"),
    ],
  },
  {
    list: "SCMSurveys",
    description: "Staff survey definitions",
    columns: [col("Status", "text", { indexed: true }), col("SurveyJson", "note")],
  },
  {
    list: "SCMSurveyAssignments",
    description: "Staff survey shares (one per staff per round)",
    columns: [
      col("Title", "text", { indexed: true }), // staff email
      col("SurveyId", "text", { indexed: true }),
      col("StaffName"), col("Department"), col("Round", "number"),
      col("SharedBy"), col("SharedByName"), col("SharedAt"), col("DueDate"),
    ],
  },
  {
    list: "SCMSurveyResponses",
    description: "Staff survey responses",
    columns: [
      col("Title", "text", { indexed: true }), // respondent email
      col("SurveyId", "text", { indexed: true }),
      col("AssignmentId", "text", { indexed: true }),
      col("RespondentName"), col("Department"), col("Round", "number"),
      col("AnswersJson", "note"), col("SubmittedAt"), col("DurationSec", "number"),
    ],
  },
];

// ── Graph helpers ──────────────────────────────────────────────────────────────

export async function getProvisionToken(instance, accounts, { interactive = false } = {}) {
  try {
    const r = await instance.acquireTokenSilent({ ...provisionRequest, account: accounts[0] });
    return r.accessToken;
  } catch (err) {
    if (!interactive) {
      const e = new Error("Sites.Manage.All consent is required to provision SharePoint lists.");
      e.code = "consent_required";
      throw e;
    }
    const r = await instance.acquireTokenPopup({ ...provisionRequest, account: accounts[0] });
    return r.accessToken;
  }
}

async function graph(token, url, { method = "GET", body } = {}) {
  const res = await fetch(`${graphBase}${url}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return null;
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(json.error?.message || `HTTP ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return json;
}

function columnPayload(c) {
  const base = { name: c.name, displayName: c.name, enforceUniqueValues: false, hidden: false };
  if (c.indexed && c.type !== "note") base.indexed = true;
  switch (c.type) {
    case "note":    return { ...base, text: { allowMultipleLines: true, linesForEditing: 6, textType: "plain", appendChangesToExistingText: false } };
    case "number":  return { ...base, number: {} };
    case "boolean": return { ...base, boolean: {} };
    case "date":    return { ...base, dateTime: { format: "dateOnly", displayAs: "default" } };
    default:        return { ...base, text: { allowMultipleLines: false, maxLength: 255 } };
  }
}

function actualType(column) {
  if (column.text) return column.text.allowMultipleLines ? "note" : "text";
  if (column.number || column.currency) return "number";
  if (column.boolean) return "boolean";
  if (column.dateTime) return "date";
  return Object.keys(column).find((k) => ["choice", "lookup", "personOrGroup", "calculated", "hyperlinkOrPicture"].includes(k)) || "other";
}

// Text and note hold the same string values, so either is compatible with the code
function compatible(expected, actual) {
  if (expected === actual) return true;
  if (expected === "text" && (actual === "note" || actual === "choice")) return true;
  // ISO date strings round-trip through either a date or a text column
  if ((expected === "date" && actual === "text") || (expected === "text" && actual === "date")) return true;
  return false;
}

// ── Provisioning ───────────────────────────────────────────────────────────────

/**
 * Run (or dry-run) provisioning against the site.
 * Returns { ok, version, ranAt, dryRun, lists: [{ list, status, actions[], warnings[], error }] }.
 */
export async function provisionSchema(token, { dryRun = false, onProgress } = {}) {
  const siteId = await getSiteId(token);
  const report = [];

  for (let i = 0; i < APP_SCHEMA.length; i++) {
    const def = APP_SCHEMA[i];
    const entry = { list: def.list, status: "ok", actions: [], warnings: [], error: null };
    onProgress?.(def.list, i, APP_SCHEMA.length);
    try {
      let list = null;
      try {
        list = await graph(token, `/sites/${siteId}/lists/${encodeURIComponent(def.list)}?$select=id,displayName`);
      } catch (err) {
        if (err.status !== 404) throw err;
      }

      if (!list) {
        entry.status = "created";
        entry.actions.push(`Create list with ${def.columns.filter((c) => c.name !== "Title").length} columns`);
        if (!dryRun) {
          list = await graph(token, `/sites/${siteId}/lists`, {
            method: "POST",
            body: { displayName: def.list, description: def.description, list: { template: "genericList" } },
          });
        }
      }

      if (list) {
        const { value: existing = [] } = await graph(token, `/sites/${siteId}/lists/${list.id}/columns`);
        const find = (name) => existing.find((c) => c.name?.toLowerCase() === name.toLowerCase() || c.displayName?.toLowerCase() === name.toLowerCase());

        for (const c of def.columns) {
          const current = find(c.name);
          if (!current) {
            if (c.name === "Title") continue; // every generic list has Title
            if (entry.status === "ok") entry.status = "updated";
            entry.actions.push(`Add column ${c.name} (${c.type})`);
            if (!dryRun) await graph(token, `/sites/${siteId}/lists/${list.id}/columns`, { method: "POST", body: columnPayload(c) });
            continue;
          }

          const type = actualType(current);
          if (!compatible(c.type, type)) {
            if (c.type === "note" && type === "text") {
              // JSON blobs truncate at 255 characters in single-line text — widening is lossless
              if (entry.status === "ok") entry.status = "updated";
              entry.actions.push(`Widen ${c.name} to multi-line text`);
              if (!dryRun) {
                try {
                  await graph(token, `/sites/${siteId}/lists/${list.id}/columns/${current.id}`, {
                    method: "PATCH",
                    body: { text: { allowMultipleLines: true, linesForEditing: 6, textType: "plain" } },
                  });
                } catch (err) {
                  entry.warnings.push(`Could not widen ${c.name}: ${err.message}. Change it to “Multiple lines of text” in list settings.`);
                }
              }
            } else {
              entry.warnings.push(`${c.name} is ${type}, expected ${c.type} — left unchanged`);
            }
          }

          if (c.indexed && c.type !== "note" && !current.indexed && actualType(current) !== "note") {
            if (entry.status === "ok") entry.status = "updated";
            entry.actions.push(`Index ${c.name}`);
            if (!dryRun) {
              try {
                await graph(token, `/sites/${siteId}/lists/${list.id}/columns/${current.id}`, { method: "PATCH", body: { indexed: true } });
              } catch (err) {
                entry.warnings.push(`Could not index ${c.name}: ${err.message}`);
              }
            }
          }
        }
      }
    } catch (err) {
      entry.status = "error";
      entry.error = err.status === 403
        ? "Access denied — your account needs Full Control / Manage Lists on the site and the app needs Sites.Manage.All consent."
        : err.message;
    }
    report.push(entry);
  }

  onProgress?.(null, APP_SCHEMA.length, APP_SCHEMA.length);
  const result = { ok: report.every((r) => r.status !== "error"), version: SCHEMA_VERSION, ranAt: new Date().toISOString(), dryRun, lists: report };
  if (!dryRun) saveProvisionResult(result);
  return result;
}

export function loadProvisionResult() {
  try { return JSON.parse(localStorage.getItem(RESULT_KEY) || "null"); } catch { return null; }
}

function saveProvisionResult(result) {
  try { localStorage.setItem(RESULT_KEY, JSON.stringify(result)); } catch {}
}

export function needsAutoProvision() {
  const last = loadProvisionResult();
  return !last || last.version !== SCHEMA_VERSION || !last.ok;
}

export const PROVISIONED_EVENT = "lekoil:schema-provisioned";
